/*
 * ===============================================================================================================
 *                                Copyright 2021, Blue Yonder Group, Inc.
 *                                           All Rights Reserved
 *
 *                               THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
 *                                          BLUE YONDER GROUP, INC.
 *
 *
 *                         The copyright notice above does not evidence any actual
 *                                 or intended publication of such source code.
 *
 * ===============================================================================================================
 */
import { Fragment, useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { Box, FormHelperText, Grid, IconButton, TextField, Tooltip } from '@mui/material';
import { LuiSelect } from '@jda/lui-common-component-library-mui5';
import { useDispatch, useSelector } from 'react-redux';
import { getAttributes, getAttributesFormError, getAttributesList, getRemoteDataUrlObjectSelector, getRemoteDataUrlResponseSelector } from '../../../store/role/policyEntity/entitySelector';
import { resetRemoteUrlResponseData, setAttributeFormData, setAttributeFormErrorData } from '../../../store/role/policyEntity/entityActionCreator';
import { useStyles } from '../styles';
import {
  DELETE,
  DISPLAY_NAME,
  DROPDOWN_MAX_HEIGHT,
  ENTITY_ATTRIBUTE_LABEL,
  ENTITY_OPERATOR_LABEL,
  ENTITY_VALUE_LABEL,
  OPERATOR_ITEMS,
  POLICY_ACTIONS_LABEL,
  SELECT_ALL
} from '../constants';
import { ArrowTrendDownFill, ArrowTrendUpFill, Trash } from '@jda/lui-common-icon-library-mui5';
import { IAttributeProps } from '../../../models/Policies/PolicyContract';
import { compareByProperty } from '../../../utility/commonUtility';
import { SearchableList } from '../../CustomList';
import { CommonFunctions } from './EntityPolicy';
import LuiMultiSelectCustom from '../../../shared/LuiMultiSelectCustom';
import _ from 'lodash';
import CustomSearchList from '../../customSearchList/CustomSearchList';
import { getRemoteDataUrlResult } from '../../../store/role/policyEntity/entities';
import { isEmpty } from '../../../utility/isEmpty';

const sortedOperatorItems = [...OPERATOR_ITEMS].sort(compareByProperty(DISPLAY_NAME));

const Attribute = ({ attribute, index, entityName, actions, edit }: IAttributeProps) => {

  const { classes } = useStyles();
  const dispatch = useDispatch();
  const attributes = useSelector(getAttributes);
  const attributeList = useSelector(getAttributesList);
  const attributesFormError = useSelector(getAttributesFormError);
  const [valueTextFieldType, setValueTextFieldType] = useState(true);
  const [enumValue, setEnumValue] = useState([{ key: 'select', displayName: 'Select' }]);
  const remoteDataUrlResponse = useSelector(getRemoteDataUrlResponseSelector);
  const [selectedAttributeValue, setSelectedAttributeValue] = useState({});
  const [openSearchAttributeValue, setOpenSearchAttributeValue] = useState(false);
  const [searchString, setSearchString] = useState('');
  const textFieldRef = useRef<HTMLInputElement>(null);
  const ignoreBlurRef = useRef(false);
  const remoteUrlDataObject = useSelector(getRemoteDataUrlObjectSelector);

  const selectedOperator = useMemo(() => sortedOperatorItems.find(item => item.key === attribute.operator?.key), [
    attribute.operator?.key
  ]);

  const getAllAttributeRemoteDataList = async () => {
    const payload = {
      pageNo: 1,
      searchString: '',
      attribute: attribute.attribute.name,
      pageSize: 50,
    }
    await getRemoteDataUrlResult(dispatch, remoteUrlDataObject, payload);
  }

  useEffect(() => {
    setSelectedAttributeValue('');
    setSearchString('');
    if (!isEmpty(remoteUrlDataObject) && attribute.attribute.name) {
      getAllAttributeRemoteDataList();
    };
  }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    , [attribute.attribute.name]);

  const handleGoToTheNextPage = useCallback(async (pageNo: number) => {
    if (pageNo !== remoteDataUrlResponse.pageNumber) {
      const payload = {
        pageNo: pageNo,
        searchString: '',
        attribute: attribute.attribute.name,
        pageSize: 50,
      }
      await getRemoteDataUrlResult(dispatch, remoteUrlDataObject, payload);;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attribute.attribute.name, remoteDataUrlResponse.pageNumber])

  const onSearchAttributeValue = (searchValue: string) => {
    const payload = {
      pageNo: 1,
      searchString: searchValue,
      attribute: attribute.attribute.name,
      pageSize: 50,
    }
    if (searchValue.trim() !== "" && searchValue.length > 3) {
      getRemoteDataUrlResult(dispatch, remoteUrlDataObject, payload);;
    }
    if (!searchValue.trim().length) {
      getAllAttributeRemoteDataList();
    }
  }

  const debounceFn = useCallback(_.debounce(onSearchAttributeValue, 500), [attribute.attribute.name]);

  const handleChange = (e: any) => {
    setSearchString(e.target.value);
    debounceFn(e.target.value);
  };

  const onFocusAttributeValue = () => {
    setOpenSearchAttributeValue(true);
    dispatch(resetRemoteUrlResponseData(true));
    getAllAttributeRemoteDataList();
  }

  const onSelectAttributeValue = (value?: any) => {
    ignoreBlurRef.current = true;
    setSelectedAttributeValue(value);
    getAllAttributeRemoteDataList();
    setSearchString(value.displayName);
    setOpenSearchAttributeValue(false);
    setTimeout(() => {
      ignoreBlurRef.current = false;
    }, 0);
    CommonFunctions(
      attributes,
      attributesFormError,
      setAttributeFormData,
      setAttributeFormErrorData,
      attributeList,
      dispatch,
      index
    ).onSelectionChange('value', index, { target: { name: '', value: value.displayName } })
  }

  const handleClose = () => {
    if (ignoreBlurRef.current) return;
    if (selectedAttributeValue !== '' && (selectedAttributeValue !== searchString)) {
      onSelectAttributeValue(selectedAttributeValue);
    }
    else if (searchString && (selectedAttributeValue !== searchString)) {
      setSearchString('')
      getAllAttributeRemoteDataList();
      setOpenSearchAttributeValue(false);
    }
    else {
      setOpenSearchAttributeValue(false);
    }
  };

  const handleToggleSearch = () => {
    if (!openSearchAttributeValue && textFieldRef.current) {
      textFieldRef.current.focus();
    }
    setOpenSearchAttributeValue(!openSearchAttributeValue);
  };

  const loading =
    entityName && entityName !== 'location' ? false : attributeList && attributeList.length > 0 ? false : true;
  let entityAttributes = attributeList.sort((a: any, b: any) =>
    a.displayName.toUpperCase().localeCompare(b.displayName.toUpperCase())
  );

  useEffect(() => {
    if (edit && attributes[index].value && attributes[index].value.length > 0) {
      const selectedAttribute = _.find(attributeList, { 'key': attributes[index].attribute.key || "" });
      if (selectedAttribute && selectedAttribute.value && selectedAttribute.value.enum) {
        const enums = selectedAttribute.value.enum.map((item: any) => {
          return {
            key: item,
            displayName: item
          };
        });
        if (attributes[index].value[0].key) {
          return
        }
        const selectedEnum = attributes[index].value.map((item: any) => {
          return {
            key: item,
            displayName: item
          };
        });
        attributes[index].value = selectedEnum;
        setValueTextFieldType(false);
        dispatch(setAttributeFormData(attributes));
        setEnumValue(enums);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attributeList, edit]);

  return (
    <Grid container alignItems="flex-end" justifyContent="space-between">
      <Grid item xs={4} className={classes.formStyle}>
        <SearchableList
          id="autocomplete-search-data-attribute"
          options={entityAttributes}
          classes={classes}
          value={attribute.attribute || undefined}
          handleChange={
            CommonFunctions(
              attributes,
              attributesFormError,
              setAttributeFormData,
              setAttributeFormErrorData,
              attributeList,
              dispatch,
              index,
              setEnumValue,
              setValueTextFieldType
            ).searchableListSelection
          }
          loading={loading}
          title={ENTITY_ATTRIBUTE_LABEL}
        />
        {attributesFormError[index] ?
          <FormHelperText id={`attribute-helper-text-${index}`} error={attributesFormError[index]?.attribute !== '' || false}>
            {attributesFormError[index].attribute || ''}
          </FormHelperText> : null
        }
      </Grid>
      <Grid item xs={2} className={classes.formStyle}>
        <LuiSelect
          maxHeight={DROPDOWN_MAX_HEIGHT}
          id="entity-operator-dropdown"
          onSelectionChange={event =>
            CommonFunctions(
              attributes,
              attributesFormError,
              setAttributeFormData,
              setAttributeFormErrorData,
              attributeList,
              dispatch,
              index
            ).onSelectionChange('operator', index, { target: { name: '', value: event.target.value } })
          }
          classes={{ select: classes.blockOperator }}
          itemData={sortedOperatorItems}
          inputLabel={ENTITY_OPERATOR_LABEL}
          disabled={false}
          selectedItem={selectedOperator}
        />
        {attributesFormError[index] ?
          <FormHelperText
            id={`attribute-action-helper-text-${index}`}
            error={attributesFormError[index].attributeAction !== ''}
          >
            {attributesFormError[index].attributeAction}
          </FormHelperText> : null
        }
      </Grid>
      <Grid item xs={2} className={classes.formStyle}>
        {valueTextFieldType ? (
          !remoteDataUrlResponse.isPresent ?
            <TextField
              id={`attribute-value-${index}`}
              name="attributeValue"
              label={ENTITY_VALUE_LABEL}
              variant="standard"
              style={{ marginBottom: '3px' }}
              fullWidth
              helperText=""
              value={attribute.value}
              onChange={e =>
                CommonFunctions(
                  attributes,
                  attributesFormError,
                  setAttributeFormData,
                  setAttributeFormErrorData,
                  attributeList,
                  dispatch,
                  index
                ).onSelectionChange('value', index, { target: { name: '', value: e.target.value } })
              }
            />
            :
            <>
              <Box className={classes.searchBox}>
                <TextField
                  variant="standard"
                  label={ENTITY_VALUE_LABEL}
                  name="attributeDropdown"
                  id={`attribute-value-textfield-${index}`}
                  focused={openSearchAttributeValue}
                  className={classes.textField}
                  onBlur={handleClose}
                  inputRef={textFieldRef}
                  onFocus={onFocusAttributeValue}
                  onChange={handleChange}
                  value={searchString}
                  helperText=""
                  style={{ marginBottom: '3px' }}
                  InputProps={openSearchAttributeValue ? {
                    endAdornment: <IconButton onClick={handleToggleSearch} size='small'><ArrowTrendUpFill color='action' fontSize='small' /></IconButton >
                  } : {
                    endAdornment: <IconButton data-TestId="downArrowAttribute" onClick={handleToggleSearch} size='small'><ArrowTrendDownFill color='action' fontSize='small' /></IconButton>
                  }}
                >
                </TextField>
                {openSearchAttributeValue &&
                  <CustomSearchList
                    pagination={{ pageNumber: remoteDataUrlResponse.pageNumber, totalCount: remoteDataUrlResponse.totalCount }}
                    list={remoteDataUrlResponse.data}
                    onSelectItem={onSelectAttributeValue}
                    goToTheNextPage={handleGoToTheNextPage}
                    selectedItem={selectedAttributeValue}
                    onMouseDown={() => (ignoreBlurRef.current = true)}
                    maxHeight={150}
                  />
                }
              </Box>
            </>
        ) : (
          <Fragment>
            <LuiMultiSelectCustom
              id={`entity-value-multi-select-${index}`}
              itemData={enumValue.sort(compareByProperty(DISPLAY_NAME))}
              itemKey="key"
              itemDisplayName={DISPLAY_NAME}
              inputLabel={ENTITY_VALUE_LABEL}
              selectAll={true}
              disabled={false}
              selectAllDisplayText={SELECT_ALL}
              selectedItems={attributes[index].value || []}
              placeHolder={ENTITY_VALUE_LABEL}
              onSelectionChange={(event: any) =>
                CommonFunctions(
                  attributes,
                  attributesFormError,
                  setAttributeFormData,
                  setAttributeFormErrorData,
                  attributeList,
                  dispatch,
                  index
                ).onSelectionChange('value', index, { target: { name: '', value: event } })
              }
            />
            <FormHelperText
              id={`attribute-value-helper-text-${index}`}
              error={attributesFormError[index].value && attributesFormError[index].value.length > 0}
            >
              {attributesFormError[index].value[0]}
            </FormHelperText>
          </Fragment>
        )}
      </Grid>
      <Grid item xs={2} className={classes.formStyle}>
        <LuiMultiSelectCustom
          customId={`attribute-value-action-multi-select-${index}`}
          itemData={actions.sort(compareByProperty(DISPLAY_NAME))}
          itemKey="key"
          itemDisplayName={DISPLAY_NAME}
          inputLabel={POLICY_ACTIONS_LABEL}
          placeHolder={POLICY_ACTIONS_LABEL}
          selectedItems={attribute && attribute.valueAction}
          searchProps={{
            enableSearch: false,
            searchPlaceholder: 'Search',
          }}
          selectAll
          selectAllDisplayText={SELECT_ALL}
          onSelectionChange={(e: any) =>
            CommonFunctions(
              attributes,
              attributesFormError,
              setAttributeFormData,
              setAttributeFormErrorData,
              attributeList,
              dispatch,
              index
            ).onSelectionChange('valueAction', index, { target: { name: '', value: e } })
          }
        />
        {attributesFormError[index] ?
          <FormHelperText
            id={`attribute-value-action-helper-text-${index}`}
            error={attributesFormError[index].valueAction !== ''}
          >
            {attributesFormError[index].valueAction}
          </FormHelperText> : null
        }
      </Grid>
      <Grid item xs={1} className={classes.deleteIcon}>
        <Tooltip id="attribute-icon-button-tooltip" title={DELETE}>
          <IconButton
            id="attribute-icon-button"
            aria-label="delete"
            onClick={() =>
              CommonFunctions(
                attributes,
                attributesFormError,
                setAttributeFormData,
                setAttributeFormErrorData,
                attributeList,
                dispatch,
                index
              ).handleRowDelete(index)
            }
          >
            <Trash />
          </IconButton>
        </Tooltip>
      </Grid>
    </Grid>
  );
};

export default Attribute;